/**
 * useLiveSync — VÉRAULT Member Suite centralized sync (Wave 30 · Phase 7c)
 * ───────────────────────────────────────────────────────────────────────
 * Single source of truth for live-update intelligence. Polls the
 * lightweight `/sync-state` endpoint every 60s (visibility-aware) and
 * emits domain-specific Sonner toasts + window custom events when any
 * domain's `updated_at` advances.
 *
 * Replaces the scattered per-page polling from Phases 3–6 (Essentials
 * 60s · Travel-Prep 90s · ExperienceLayout 45s · Memories n/a).
 *
 * Surfaces:
 *   - Custom events: `vt:sync-changed:{domain}` (dispatched on window)
 *   - Sonner toasts (de-duplicated by domain, max 1 per 45 seconds)
 *   - Visibility-aware (paused when document.hidden)
 *
 * Toast taxonomy (calm + premium):
 *   trip:         "Your experience has been updated."
 *   itinerary:    "Your itinerary has been updated."
 *   essentials:   "Your essentials have been refreshed."
 *   travel_prep:  "Your travel prep has refreshed."
 *   balances:     "Your balance has been updated."
 *   approval:     "Your revised experience is ready for review."
 *
 * Usage:
 *   useLiveSync(tripId);                         // mount; emits events globally
 *   useEffect(() => {
 *     const refetch = () => fetchEssentials();
 *     window.addEventListener('vt:sync-changed:essentials', refetch);
 *     return () => window.removeEventListener('vt:sync-changed:essentials', refetch);
 *   }, [fetchEssentials]);
 */
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";

const POLL_INTERVAL_MS = 30000;
const TOAST_DEDUP_MS = 45000;

const DOMAINS = [
  "trip",
  "itinerary",
  "essentials",
  "travel_prep",
  "balances",
  "approval",
  // Wave 30.2 · Phase 8e — Messages domain (last_message_at across user's threads)
  "messages",
];

const TOAST_COPY = {
  trip: {
    title: "Your experience has been updated.",
    description: "Quiet sync · new details available.",
  },
  itinerary: {
    title: "Your itinerary has been updated.",
    description: "Your advisor has shaped a moment of your trip.",
  },
  essentials: {
    title: "Your essentials have been refreshed.",
    description: "Transportation · stays · documents — quietly in sync.",
  },
  travel_prep: {
    title: "Your travel prep has refreshed.",
    description: "Packing · briefing · notes — quietly in sync.",
  },
  balances: {
    title: "Your balance has been updated.",
    description: "Quiet sync · review when you're ready.",
  },
  approval: {
    title: "Your revised experience is ready for review.",
    description: "Open your itinerary to review and approve.",
  },
  // Wave 30.2 · Phase 8e — Messages domain.
  messages: {
    title: "A new message has arrived.",
    description: "From your advisor.",
  },
};

/**
 * useLiveSync — mount once per experience scope (e.g. in ExperienceLayout
 * or Overview). All other pages can listen to the dispatched window events
 * without polling themselves.
 *
 * @param {string} tripId
 * @param {object} options
 *   - intervalMs: override poll cadence (default 60s)
 *   - silentFirstRun: if true, doesn't emit events on the initial fetch
 *     (recommended — first hit is the baseline, not a change)
 */
export function useLiveSync(tripId, options = {}) {
  const { intervalMs = POLL_INTERVAL_MS, silentFirstRun = true } = options;
  const prev = useRef({});
  const lastToastAt = useRef({});
  const isFirstRun = useRef(true);

  useEffect(() => {
    if (!tripId) return undefined;
    let cancelled = false;
    let timer = null;
    let visibilityHandler = null;

    const emitDomainChange = (domain) => {
      // Window event — pages re-fetch their data on this signal.
      window.dispatchEvent(new CustomEvent(`vt:sync-changed:${domain}`, {
        detail: { tripId, domain, at: new Date().toISOString() },
      }));

      // Toast (de-duplicated per domain per 45s).
      const last = lastToastAt.current[domain] || 0;
      const now = Date.now();
      if (now - last < TOAST_DEDUP_MS) return;
      lastToastAt.current[domain] = now;
      const copy = TOAST_COPY[domain];
      if (copy) {
        toast(copy.title, {
          description: copy.description,
          duration: 4200,
        });
      }
    };

    const pollOnce = async () => {
      try {
        const res = await api.get(`/client-portal/experiences/${tripId}/sync-state`);
        if (cancelled) return;
        const data = res.data || {};
        const changed = [];
        for (const domain of DOMAINS) {
          const key = `${domain}_updated_at`;
          const newVal = data[key];
          const oldVal = prev.current[key];
          if (newVal && oldVal && newVal !== oldVal) {
            changed.push(domain);
          }
          if (newVal !== undefined) {
            prev.current[key] = newVal;
          }
        }
        if (!isFirstRun.current || !silentFirstRun) {
          for (const domain of changed) emitDomainChange(domain);
        }
        isFirstRun.current = false;
      } catch (err) {
        // Calm failure — offline/SW will serve a 503 marker. We don't toast
        // on poll failure (the OfflineBanner already conveys it).
      }
    };

    const schedule = () => {
      timer = window.setTimeout(async () => {
        if (document.visibilityState === "visible") {
          await pollOnce();
        }
        schedule();
      }, intervalMs);
    };

    // Initial poll after a short settle delay.
    const initial = window.setTimeout(pollOnce, 2500);
    schedule();

    // Visibility change — fire an immediate catch-up poll when the tab
    // returns to visible (battery-friendly + responsive).
    visibilityHandler = () => {
      if (document.visibilityState === "visible") {
        pollOnce();
      }
    };
    document.addEventListener("visibilitychange", visibilityHandler);

    return () => {
      cancelled = true;
      window.clearTimeout(initial);
      if (timer) window.clearTimeout(timer);
      if (visibilityHandler) {
        document.removeEventListener("visibilitychange", visibilityHandler);
      }
    };
  }, [tripId, intervalMs, silentFirstRun]);
}

export default useLiveSync;
